import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Shield, AlertTriangle, Footprints, UserPlus, Wrench, KeyRound, Plus, CheckCircle2, Trash2, LogOut as LogOutIcon } from 'lucide-react';
import { toast } from 'sonner';
import BuildingFilter from '../components/BuildingFilter';

const INCIDENT_CATEGORIES = ['theft', 'vandalism', 'fire', 'accident', 'trespass', 'fight', 'medical', 'power_outage', 'leak', 'elevator_stuck', 'other'];
const INCIDENT_SEVERITIES = ['low', 'medium', 'high', 'critical'];
const VISIT_PURPOSES = ['personal', 'delivery', 'contractor', 'maintenance', 'inspection', 'official', 'viewing', 'other'];
const ID_TYPES = ['Emirates ID', 'Passport', 'Driving Licence', 'Other'];
const SHIFTS = ['day', 'evening', 'night'];

const SEVERITY_COLOR = {
  low: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  critical: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
};

const VALID_TABS = ['incidents', 'patrols', 'visitors', 'maint-visits', 'keys'];

export default function Security() {
  const { tab: routeTab } = useParams();
  const navigate = useNavigate();
  const tab = VALID_TABS.includes(routeTab) ? routeTab : 'incidents';
  const { user } = useContext(AuthContext);

  const [summary, setSummary] = useState({});
  const [buildings, setBuildings] = useState([]);
  const [buildingFilter, setBuildingFilter] = useState('all');

  const [incidents, setIncidents] = useState([]);
  const [patrols, setPatrols] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [maintVisits, setMaintVisits] = useState([]);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const bQ = buildingFilter !== 'all' ? `?building_id=${buildingFilter}` : '';
      const [s, b, i, p, v, m, k] = await Promise.all([
        axios.get(`${API}/security/summary${bQ}`, { withCredentials: true }),
        axios.get(`${API}/buildings`, { withCredentials: true }),
        axios.get(`${API}/security/incidents${bQ}`, { withCredentials: true }),
        axios.get(`${API}/security/patrols${bQ}`, { withCredentials: true }),
        axios.get(`${API}/security/visitors${bQ}`, { withCredentials: true }),
        axios.get(`${API}/security/maint-visits${bQ}`, { withCredentials: true }),
        axios.get(`${API}/security/keys${bQ}`, { withCredentials: true }),
      ]);
      setSummary(s.data || {});
      setBuildings(b.data || []);
      setIncidents(i.data || []);
      setPatrols(p.data || []);
      setVisitors(v.data || []);
      setMaintVisits(m.data || []);
      setKeys(k.data || []);
    } catch (e) {
      toast.error('Failed to load security data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [buildingFilter]);

  const switchTab = (v) => navigate(`/security/${v}`);

  return (
    <Layout>
      <div data-testid="security-page" className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <Shield className="w-7 h-7 text-indigo-500" /> Security Operations
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Incident reporting, patrol log, visitor & contractor sign-in, key register.
            </p>
          </div>
          <BuildingFilter value={buildingFilter} onChange={setBuildingFilter} buildings={buildings} />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          <SummaryCard label="Open Incidents" value={summary.open_incidents} color="rose" testId="sec-open-incidents" />
          <SummaryCard label="Critical" value={summary.critical_incidents} color="rose" testId="sec-critical" />
          <SummaryCard label="Patrols Today" value={summary.patrols_today} color="indigo" testId="sec-patrols-today" />
          <SummaryCard label="In Progress" value={summary.patrols_in_progress} color="amber" testId="sec-patrols-in-progress" />
          <SummaryCard label="Visitors On Site" value={summary.visitors_on_site} color="sky" testId="sec-visitors-on-site" />
          <SummaryCard label="Contractors On Site" value={summary.contractors_on_site} color="violet" testId="sec-contractors-on-site" />
          <SummaryCard label="Keys Issued" value={summary.keys_issued} color="emerald" testId="sec-keys-issued" />
          <SummaryCard label="Keys Lost" value={summary.keys_lost} color="rose" testId="sec-keys-lost" />
        </div>

        <Tabs value={tab} onValueChange={switchTab}>
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="incidents" data-testid="sec-tab-incidents"><AlertTriangle className="w-4 h-4 mr-1" /> Incidents ({incidents.length})</TabsTrigger>
            <TabsTrigger value="patrols" data-testid="sec-tab-patrols"><Footprints className="w-4 h-4 mr-1" /> Patrol Log ({patrols.length})</TabsTrigger>
            <TabsTrigger value="visitors" data-testid="sec-tab-visitors"><UserPlus className="w-4 h-4 mr-1" /> Visitors ({visitors.length})</TabsTrigger>
            <TabsTrigger value="maint-visits" data-testid="sec-tab-maint-visits"><Wrench className="w-4 h-4 mr-1" /> Maintenance Visits ({maintVisits.length})</TabsTrigger>
            <TabsTrigger value="keys" data-testid="sec-tab-keys"><KeyRound className="w-4 h-4 mr-1" /> Key Register ({keys.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="incidents" className="mt-4">
            <IncidentsTab incidents={incidents} buildings={buildings} loading={loading} refresh={fetchAll} />
          </TabsContent>
          <TabsContent value="patrols" className="mt-4">
            <PatrolsTab patrols={patrols} buildings={buildings} loading={loading} refresh={fetchAll} user={user} />
          </TabsContent>
          <TabsContent value="visitors" className="mt-4">
            <VisitorsTab visitors={visitors} buildings={buildings} loading={loading} refresh={fetchAll} />
          </TabsContent>
          <TabsContent value="maint-visits" className="mt-4">
            <MaintVisitsTab visits={maintVisits} buildings={buildings} loading={loading} refresh={fetchAll} />
          </TabsContent>
          <TabsContent value="keys" className="mt-4">
            <KeysTab keys={keys} buildings={buildings} loading={loading} refresh={fetchAll} />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

function SummaryCard({ label, value, color, testId }) {
  const c = {
    rose: 'text-rose-600 dark:text-rose-400',
    indigo: 'text-indigo-600 dark:text-indigo-400',
    amber: 'text-amber-600 dark:text-amber-400',
    sky: 'text-sky-600 dark:text-sky-400',
    violet: 'text-violet-600 dark:text-violet-400',
    emerald: 'text-emerald-600 dark:text-emerald-400',
  }[color];
  return (
    <Card data-testid={testId} className="p-3">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${c}`}>{value ?? 0}</div>
    </Card>
  );
}

// ─────────────────── INCIDENTS TAB ───────────────────
function IncidentsTab({ incidents, buildings, loading, refresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const bName = (id) => buildings.find(b => b.id === id)?.name || '—';

  const save = async () => {
    if (!form.description) return toast.error('Description is required');
    try {
      await axios.post(`${API}/security/incidents`, {
        ...form,
        occurred_at: form.occurred_at || new Date().toISOString(),
      }, { withCredentials: true });
      toast.success('Incident logged');
      setOpen(false); setForm({}); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const resolve = async (id) => {
    const resolution = window.prompt('Resolution notes:');
    if (resolution === null) return;
    try {
      await axios.put(`${API}/security/incidents/${id}`, { status: 'resolved', resolution }, { withCredentials: true });
      toast.success('Incident resolved');
      refresh();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-incident-btn"><Plus className="w-4 h-4 mr-1" /> New Incident</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Log Incident</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category *</Label>
                  <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="inc-category"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{INCIDENT_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Severity *</Label>
                  <Select value={form.severity || 'medium'} onValueChange={v => setForm({ ...form, severity: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{INCIDENT_SEVERITIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Building</Label>
                <Select value={form.building_id || 'none'} onValueChange={v => setForm({ ...form, building_id: v === 'none' ? null : v })}>
                  <SelectTrigger><SelectValue placeholder="Pick a building" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {buildings.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Location detail</Label><Input data-testid="inc-location" value={form.location_detail || ''} onChange={e => setForm({ ...form, location_detail: e.target.value })} placeholder="e.g. B2 Parking near elevator" /></div>
              <div><Label>Occurred at</Label><Input type="datetime-local" value={form.occurred_at || ''} onChange={e => setForm({ ...form, occurred_at: e.target.value })} /></div>
              <div><Label>Description *</Label><Textarea data-testid="inc-description" rows={3} value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What happened?" /></div>
              <div><Label>Persons involved</Label><Input value={form.persons_involved || ''} onChange={e => setForm({ ...form, persons_involved: e.target.value })} /></div>
              <div><Label>Witnesses</Label><Input value={form.witnesses || ''} onChange={e => setForm({ ...form, witnesses: e.target.value })} /></div>
              <div><Label>Actions taken</Label><Textarea rows={2} value={form.actions_taken || ''} onChange={e => setForm({ ...form, actions_taken: e.target.value })} /></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.police_called} onChange={e => setForm({ ...form, police_called: e.target.checked })} /> Police was called</label>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="save-incident-btn" onClick={save}>Log Incident</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {loading ? <div className="text-muted-foreground text-sm">Loading…</div> :
        incidents.length === 0 ? <Card className="p-8 text-center text-muted-foreground">No incidents logged yet.</Card> :
          incidents.map(inc => (
            <Card key={inc.id} data-testid={`incident-${inc.id}`} className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm font-bold text-indigo-600 dark:text-indigo-400">{inc.incident_no}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${SEVERITY_COLOR[inc.severity]}`}>{inc.severity}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200 uppercase">{inc.category}</span>
                    {inc.police_called && <span className="text-xs px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">Police called</span>}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${inc.status === 'resolved' || inc.status === 'closed' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'}`}>{inc.status}</span>
                  </div>
                  <div className="mt-2 text-sm text-foreground">{inc.description}</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {bName(inc.building_id)}{inc.location_detail ? ` · ${inc.location_detail}` : ''} · {inc.occurred_at?.slice(0, 16).replace('T', ' ')} · by {inc.reported_by_name}
                  </div>
                  {inc.resolution && <div className="mt-2 text-xs text-emerald-700 dark:text-emerald-300">Resolution: {inc.resolution}</div>}
                </div>
                {inc.status !== 'resolved' && inc.status !== 'closed' && (
                  <Button data-testid={`resolve-incident-${inc.id}`} size="sm" variant="outline" onClick={() => resolve(inc.id)}>
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Resolve
                  </Button>
                )}
              </div>
            </Card>
          ))
      }
    </div>
  );
}

// ─────────────────── PATROLS TAB ───────────────────
function PatrolsTab({ patrols, buildings, loading, refresh, user }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ checkpoints_text: '' });
  const bName = (id) => buildings.find(b => b.id === id)?.name || '—';

  const save = async () => {
    if (!form.started_at) return toast.error('Start time required');
    const checkpoints = (form.checkpoints_text || '').split('\n').map(s => s.trim()).filter(Boolean).map(name => ({ name, scanned_at: null }));
    try {
      await axios.post(`${API}/security/patrols`, {
        building_id: form.building_id || null,
        started_at: form.started_at,
        shift: form.shift || null,
        officer_name: user?.name,
        checkpoints,
        status: 'in_progress',
      }, { withCredentials: true });
      toast.success('Patrol started');
      setOpen(false); setForm({ checkpoints_text: '' }); refresh();
    } catch { toast.error('Failed'); }
  };

  const completePatrol = async (p) => {
    try {
      await axios.put(`${API}/security/patrols/${p.id}`, {
        status: 'completed',
        ended_at: new Date().toISOString(),
      }, { withCredentials: true });
      toast.success('Patrol completed');
      refresh();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-patrol-btn"><Plus className="w-4 h-4 mr-1" /> Start Patrol</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Start Patrol Round</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label>Building</Label>
                <Select value={form.building_id || 'none'} onValueChange={v => setForm({ ...form, building_id: v === 'none' ? null : v })}>
                  <SelectTrigger><SelectValue placeholder="Pick" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {buildings.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Started at *</Label><Input type="datetime-local" value={form.started_at || ''} onChange={e => setForm({ ...form, started_at: e.target.value })} /></div>
                <div><Label>Shift</Label>
                  <Select value={form.shift || ''} onValueChange={v => setForm({ ...form, shift: v })}>
                    <SelectTrigger><SelectValue placeholder="Pick" /></SelectTrigger>
                    <SelectContent>{SHIFTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Checkpoints (one per line)</Label><Textarea rows={4} value={form.checkpoints_text} onChange={e => setForm({ ...form, checkpoints_text: e.target.value })} placeholder={'Lobby\nGround Floor Entrance\nB1 Parking\nRooftop'} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="save-patrol-btn" onClick={save}>Start</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {loading ? <div className="text-muted-foreground text-sm">Loading…</div> :
        patrols.length === 0 ? <Card className="p-8 text-center text-muted-foreground">No patrols logged yet.</Card> :
          patrols.map(p => (
            <Card key={p.id} data-testid={`patrol-${p.id}`} className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm font-bold text-indigo-600 dark:text-indigo-400">{p.patrol_no}</span>
                    {p.shift && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200 uppercase">{p.shift}</span>}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${p.status === 'completed' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'}`}>{p.status}</span>
                  </div>
                  <div className="mt-1 text-sm text-foreground">{p.officer_name} · {bName(p.building_id)}</div>
                  <div className="text-xs text-muted-foreground">
                    {p.started_at?.slice(0, 16).replace('T', ' ')}{p.ended_at ? ' → ' + p.ended_at.slice(0, 16).replace('T', ' ') : ''}
                  </div>
                  {(p.checkpoints || []).length > 0 && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      Checkpoints: {p.checkpoints.length} ({p.checkpoints.filter(c => c.scanned_at).length} scanned)
                    </div>
                  )}
                </div>
                {p.status === 'in_progress' && (
                  <Button data-testid={`complete-patrol-${p.id}`} size="sm" onClick={() => completePatrol(p)}>
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Complete
                  </Button>
                )}
              </div>
            </Card>
          ))
      }
    </div>
  );
}

// ─────────────────── VISITORS TAB ───────────────────
function VisitorsTab({ visitors, buildings, loading, refresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const bName = (id) => buildings.find(b => b.id === id)?.name || '—';

  const save = async () => {
    if (!form.visitor_name) return toast.error('Visitor name required');
    try {
      await axios.post(`${API}/security/visitors`, {
        ...form,
        checked_in_at: form.checked_in_at || new Date().toISOString(),
        purpose: form.purpose || 'personal',
      }, { withCredentials: true });
      toast.success('Visitor checked in');
      setOpen(false); setForm({}); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const checkout = async (id) => {
    try {
      await axios.post(`${API}/security/visitors/${id}/checkout`, {}, { withCredentials: true });
      toast.success('Visitor checked out');
      refresh();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-visitor-btn"><Plus className="w-4 h-4 mr-1" /> Check In Visitor</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Visitor Check-In</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name *</Label><Input data-testid="vis-name" value={form.visitor_name || ''} onChange={e => setForm({ ...form, visitor_name: e.target.value })} /></div>
                <div><Label>Phone</Label><Input value={form.visitor_phone || ''} onChange={e => setForm({ ...form, visitor_phone: e.target.value })} /></div>
                <div><Label>ID Type</Label>
                  <Select value={form.id_type || ''} onValueChange={v => setForm({ ...form, id_type: v })}>
                    <SelectTrigger><SelectValue placeholder="Pick" /></SelectTrigger>
                    <SelectContent>{ID_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>ID Number</Label><Input value={form.id_number || ''} onChange={e => setForm({ ...form, id_number: e.target.value })} /></div>
                <div><Label>Vehicle plate</Label><Input value={form.vehicle_plate || ''} onChange={e => setForm({ ...form, vehicle_plate: e.target.value })} /></div>
                <div><Label>Purpose</Label>
                  <Select value={form.purpose || 'personal'} onValueChange={v => setForm({ ...form, purpose: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{VISIT_PURPOSES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Host name</Label><Input value={form.host_name || ''} onChange={e => setForm({ ...form, host_name: e.target.value })} /></div>
                <div><Label>Host unit #</Label><Input value={form.host_unit || ''} onChange={e => setForm({ ...form, host_unit: e.target.value })} /></div>
              </div>
              <div><Label>Building</Label>
                <Select value={form.building_id || 'none'} onValueChange={v => setForm({ ...form, building_id: v === 'none' ? null : v })}>
                  <SelectTrigger><SelectValue placeholder="Pick" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {buildings.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Notes</Label><Textarea rows={2} value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="save-visitor-btn" onClick={save}>Check In</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {loading ? <div className="text-muted-foreground text-sm">Loading…</div> :
        visitors.length === 0 ? <Card className="p-8 text-center text-muted-foreground">No visitors logged yet.</Card> :
          visitors.map(v => (
            <Card key={v.id} data-testid={`visitor-${v.id}`} className="p-4 flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-foreground">{v.visitor_name}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 uppercase">{v.purpose}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${v.status === 'checked_in' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'}`}>{v.status === 'checked_in' ? 'On site' : 'Departed'}</span>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Host: {v.host_name || '—'}{v.host_unit ? ` (${v.host_unit})` : ''} · {bName(v.building_id)}
                  {v.visitor_phone ? ` · ${v.visitor_phone}` : ''}{v.vehicle_plate ? ` · 🚗 ${v.vehicle_plate}` : ''}
                </div>
                <div className="text-xs text-muted-foreground">
                  In: {v.checked_in_at?.slice(0, 16).replace('T', ' ')}{v.checked_out_at ? ' · Out: ' + v.checked_out_at.slice(0, 16).replace('T', ' ') : ''}
                </div>
              </div>
              {v.status === 'checked_in' && (
                <Button data-testid={`checkout-visitor-${v.id}`} size="sm" variant="outline" onClick={() => checkout(v.id)}>
                  <LogOutIcon className="w-4 h-4 mr-1" /> Check Out
                </Button>
              )}
            </Card>
          ))
      }
    </div>
  );
}

// ─────────────────── MAINTENANCE VISITS TAB ───────────────────
function MaintVisitsTab({ visits, buildings, loading, refresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const bName = (id) => buildings.find(b => b.id === id)?.name || '—';

  const save = async () => {
    if (!form.contractor_name) return toast.error('Contractor name required');
    try {
      await axios.post(`${API}/security/maint-visits`, {
        ...form, checked_in_at: form.checked_in_at || new Date().toISOString(),
      }, { withCredentials: true });
      toast.success('Maintenance visit logged');
      setOpen(false); setForm({}); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const checkout = async (id) => {
    try {
      await axios.post(`${API}/security/maint-visits/${id}/checkout`, {}, { withCredentials: true });
      toast.success('Checked out'); refresh();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-maint-visit-btn"><Plus className="w-4 h-4 mr-1" /> Log Maintenance Visit</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Maintenance / Contractor Visit</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Technician / contractor name *</Label><Input data-testid="mv-name" value={form.contractor_name || ''} onChange={e => setForm({ ...form, contractor_name: e.target.value })} /></div>
                <div><Label>Company</Label><Input value={form.contractor_company || ''} onChange={e => setForm({ ...form, contractor_company: e.target.value })} /></div>
                <div><Label>Phone</Label><Input value={form.contractor_phone || ''} onChange={e => setForm({ ...form, contractor_phone: e.target.value })} /></div>
                <div><Label>Vehicle plate</Label><Input value={form.vehicle_plate || ''} onChange={e => setForm({ ...form, vehicle_plate: e.target.value })} /></div>
                <div><Label>ID Type</Label>
                  <Select value={form.id_type || ''} onValueChange={v => setForm({ ...form, id_type: v })}>
                    <SelectTrigger><SelectValue placeholder="Pick" /></SelectTrigger>
                    <SelectContent>{ID_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>ID Number</Label><Input value={form.id_number || ''} onChange={e => setForm({ ...form, id_number: e.target.value })} /></div>
                <div><Label>Work Order # (optional)</Label><Input value={form.related_wo || ''} onChange={e => setForm({ ...form, related_wo: e.target.value })} placeholder="WO/INT/..." /></div>
                <div><Label>AMC Contract (optional)</Label><Input value={form.related_amc || ''} onChange={e => setForm({ ...form, related_amc: e.target.value })} /></div>
              </div>
              <div><Label>Building</Label>
                <Select value={form.building_id || 'none'} onValueChange={v => setForm({ ...form, building_id: v === 'none' ? null : v })}>
                  <SelectTrigger><SelectValue placeholder="Pick" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {buildings.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Tools brought</Label><Input value={form.tools_brought || ''} onChange={e => setForm({ ...form, tools_brought: e.target.value })} /></div>
              <div><Label>Notes</Label><Textarea rows={2} value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="save-maint-visit-btn" onClick={save}>Log Visit</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {loading ? <div className="text-muted-foreground text-sm">Loading…</div> :
        visits.length === 0 ? <Card className="p-8 text-center text-muted-foreground">No maintenance visits logged.</Card> :
          visits.map(v => (
            <Card key={v.id} data-testid={`maint-visit-${v.id}`} className="p-4 flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-foreground">{v.contractor_name}</span>
                  {v.contractor_company && <span className="text-xs text-muted-foreground">({v.contractor_company})</span>}
                  <span className={`text-xs px-2 py-0.5 rounded-full ${v.status === 'checked_in' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'}`}>{v.status === 'checked_in' ? 'On site' : 'Departed'}</span>
                  {v.related_wo && <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 font-mono">{v.related_wo}</span>}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {bName(v.building_id)} · {v.contractor_phone || '—'}
                  {v.vehicle_plate ? ` · 🚗 ${v.vehicle_plate}` : ''}
                  {v.id_type ? ` · ${v.id_type}${v.id_number ? ' ' + v.id_number : ''}` : ''}
                </div>
                <div className="text-xs text-muted-foreground">
                  In: {v.checked_in_at?.slice(0, 16).replace('T', ' ')}{v.checked_out_at ? ' · Out: ' + v.checked_out_at.slice(0, 16).replace('T', ' ') : ''}
                </div>
              </div>
              {v.status === 'checked_in' && (
                <Button data-testid={`checkout-maint-${v.id}`} size="sm" variant="outline" onClick={() => checkout(v.id)}>
                  <LogOutIcon className="w-4 h-4 mr-1" /> Check Out
                </Button>
              )}
            </Card>
          ))
      }
    </div>
  );
}

// ─────────────────── KEYS TAB ───────────────────
function KeysTab({ keys, buildings, loading, refresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [issueDialog, setIssueDialog] = useState(null);
  const bName = (id) => buildings.find(b => b.id === id)?.name || '—';

  const save = async () => {
    if (!form.key_code || !form.label) return toast.error('Key code and label required');
    try {
      await axios.post(`${API}/security/keys`, { ...form, status: 'available' }, { withCredentials: true });
      toast.success('Key added');
      setOpen(false); setForm({}); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const issue = async (payload) => {
    try {
      await axios.post(`${API}/security/keys/${issueDialog.id}/issue`, payload, { withCredentials: true });
      toast.success('Key issued'); setIssueDialog(null); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const returnKey = async (id) => {
    if (!window.confirm('Mark this key as returned?')) return;
    try {
      await axios.post(`${API}/security/keys/${id}/return`, {}, { withCredentials: true });
      toast.success('Key returned'); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const removeKey = async (id) => {
    if (!window.confirm('Delete this key from the register?')) return;
    try {
      await axios.delete(`${API}/security/keys/${id}`, { withCredentials: true });
      toast.success('Key removed'); refresh();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-key-btn"><Plus className="w-4 h-4 mr-1" /> Add Key</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Key to Register</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Key Code *</Label><Input data-testid="key-code" value={form.key_code || ''} onChange={e => setForm({ ...form, key_code: e.target.value })} placeholder="K-001" /></div>
                <div><Label>Label *</Label><Input data-testid="key-label" value={form.label || ''} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="Master Lobby Floor 1" /></div>
              </div>
              <div><Label>Building</Label>
                <Select value={form.building_id || 'none'} onValueChange={v => setForm({ ...form, building_id: v === 'none' ? null : v })}>
                  <SelectTrigger><SelectValue placeholder="Pick" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {buildings.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Location detail</Label><Input value={form.location_detail || ''} onChange={e => setForm({ ...form, location_detail: e.target.value })} placeholder="e.g. Reception cabinet, Slot 12" /></div>
              <div><Label>Notes</Label><Textarea rows={2} value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="save-key-btn" onClick={save}>Add Key</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? <div className="text-muted-foreground text-sm">Loading…</div> :
        keys.length === 0 ? <Card className="p-8 text-center text-muted-foreground">No keys in the register yet.</Card> :
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {keys.map(k => (
              <Card key={k.id} data-testid={`key-${k.id}`} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-xs text-indigo-600 dark:text-indigo-400 font-bold">{k.key_code}</div>
                    <div className="font-semibold text-foreground truncate">{k.label}</div>
                    <div className="text-xs text-muted-foreground">{bName(k.building_id)}{k.location_detail ? ` · ${k.location_detail}` : ''}</div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${k.status === 'available' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : k.status === 'issued' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' : 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'}`}>{k.status}</span>
                </div>
                {k.status === 'issued' && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    Issued to: <span className="text-foreground font-medium">{k.issued_to_name}</span>
                    <div>Since {k.issued_at?.slice(0, 16).replace('T', ' ')}</div>
                    {k.expected_return_at && <div>Expected back: {k.expected_return_at.slice(0, 16).replace('T', ' ')}</div>}
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  {k.status === 'available' && (
                    <Button data-testid={`issue-key-${k.id}`} size="sm" onClick={() => setIssueDialog(k)}>Issue</Button>
                  )}
                  {k.status === 'issued' && (
                    <Button data-testid={`return-key-${k.id}`} size="sm" variant="outline" onClick={() => returnKey(k.id)}>Return</Button>
                  )}
                  <Button data-testid={`delete-key-${k.id}`} size="sm" variant="ghost" onClick={() => removeKey(k.id)} className="text-rose-600 ml-auto">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
      }

      {issueDialog && (
        <IssueKeyDialog k={issueDialog} onClose={() => setIssueDialog(null)} onIssue={issue} />
      )}
    </div>
  );
}

function IssueKeyDialog({ k, onClose, onIssue }) {
  const [to, setTo] = useState('');
  const [expected, setExpected] = useState('');
  const [notes, setNotes] = useState('');
  const submit = () => {
    if (!to) { toast.error('Recipient name is required'); return; }
    onIssue({ issued_to_name: to, expected_return_at: expected || null, notes });
  };
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Issue Key: {k.label}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Issue to *</Label><Input data-testid="issue-to-name" value={to} onChange={e => setTo(e.target.value)} placeholder="Name of recipient" /></div>
          <div><Label>Expected return</Label><Input type="datetime-local" value={expected} onChange={e => setExpected(e.target.value)} /></div>
          <div><Label>Notes</Label><Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button data-testid="confirm-issue-key" onClick={submit}>Issue</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
